import React, { useContext } from 'react';

import { ImagePreview } from '../images/ImagePreview';
import ImageClusterPreview from '../images/ImageClusterPreview';
import BadgeColorHash from '../common/BadgeColorHash';
import SimilarityBadge from '../common/SimilarityBadge';
import BadgeId from '../common/BadgeId';
import { CompareImagePreview } from '../images/CompareImagePreview';
import ScatterPlot2D from '../charts/ScatterPlot2D';
import { IconButton } from '../common/IconButton';
import ImageTopSimilarityFinder from '../images/ImageTopSimilarityFinder';
import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import {
  clusterPairAdminUrl,
  toOriginalImageUrl,
  FAIL_TYPE_HELP,
} from './fuseboxBenchmarkUtils';
import TrimSpec from '../lego/trims/TrimSpec';

function SummaryTable({ summary, fields }) {
  return (
    <table className="table table-sm w-auto mb-2">
      <tbody>
        {fields.map(([key, label, isPct]) => {
          const v = summary[key];
          const text = v == null
            ? '—'
            : isPct
              ? `${(v * 100).toFixed(1)}%`
              : typeof v === 'object'
                ? JSON.stringify(v)
                : String(v);
          return (
            <tr key={key}>
              <th className="small text-muted pr-3">{label}</th>
              <td className="small">{text}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function ThresholdSweep({ sweep, bestThreshold }) {
  if (!sweep?.length) return null;
  const max = Math.max(...sweep.map((s) => s.accuracy));
  return (
    <div className="mb-2" style={{ maxWidth: 460 }}>
      <div className="small text-muted">accuracy by threshold (best {bestThreshold})</div>
      {sweep.filter((s, i) => i % 2 === 0).map((s) => (
        <div key={s.threshold} className="d-flex align-items-center" style={{ lineHeight: '14px' }}>
          <span className="small text-muted mr-1" style={{ width: 42 }}>
            {s.threshold.toFixed(2)}
          </span>
          <div
            style={{
              width: `${Math.round((s.accuracy / max) * 360)}px`,
              height: 9,
              background: s.threshold === bestThreshold ? '#2563eb' : '#9ec0ee',
            }}
          />
          <span className="small ml-1">{(s.accuracy * 100).toFixed(1)}%</span>
        </div>
      ))}
    </div>
  );
}

function RunHeader({ result }) {
  return (
    <div className="small text-muted mb-2">
      embedding <strong>{result.embedding?.setupName || '?'}</strong>
      {' '}(dim {result.embedding?.dim}, {result.embedding?.total} vectors) ·
      sample {result.params?.name || 'whole collection'} · seed {result.params?.seed} ·
      threshold {result.params?.threshold}
      {result.params?.superclusterThreshold != null ? ` · supercluster thr ${result.params.superclusterThreshold}` : ''}
      {' '}· {result.generated_at}
    </div>
  );
}

const HIT_COLORS = { joined_existing: '#16a34a', new_excused: '#9d94A8', created_new: '#84cc16' };
const FAIL_COLORS = { wrong_cluster: '#dc2626', false_match: '#803613', missed_match: '#f59e0b' };
// Decisions whose pick came through linked clusters (supercluster) get a violet ring.
const SUPERCLUSTER_COLOR = '#000';

/**
 * Scatter of every clustering-test decision vs its best CANDIDATE cluster: x = best similarity,
 * y = median similarity to that cluster's pre-cutoff images.
 */
function ClusteringHitsScatter({ result }) {
  const med = (p) => p.best_cluster_median_similarity ?? p.predicted_cluster_median_similarity;
  const hits = (result.hits || []).filter((h) => h.best_similarity != null && med(h) != null);
  const fails = (result.failures || []).filter((f) => f.best_similarity != null && med(f) != null);
  if (!hits.length && !fails.length) return null;

  const points = [
    ...hits.map((h, i) => ({
      key: `h${i}`,
      x: h.best_similarity,
      y: med(h),
      color: HIT_COLORS[h.hit_type] || '#16a34a',
      fillOpacity: 0.55,
      radius: 3.5,
      ...(h.picked_supercluster_id ? { stroke: SUPERCLUSTER_COLOR, strokeWidth: 1.6 } : {}),
      title: `${h.image_id} (${h.model_id}, ${h.image_date})\n`
        + `best ${h.best_similarity} · candidate-cluster median ${med(h)}\n`
        + h.hit_type
        + (h.picked_supercluster_id ? ' · via supercluster' : ''),
    })),
    ...fails.map((f, i) => ({
      key: `f${i}`,
      x: f.best_similarity,
      y: med(f),
      color: FAIL_COLORS[f.fail_type] || '#dc2626',
      fillOpacity: 0.8,
      radius: 4,
      stroke: f.picked_supercluster_id ? SUPERCLUSTER_COLOR : '#7f1d1d',
      strokeWidth: f.picked_supercluster_id ? 1.6 : 0,
      title: `${f.image_id} (${f.model_id}, ${f.image_date})\n`
        + `best ${f.best_similarity} · candidate-cluster median ${med(f)}\n`
        + `${f.fail_type}: ${f.predicted_cluster_id ? `joined ${f.predicted_cluster_id}` : 'said new'}, `
        + `actual ${f.actual === 'new' ? 'new cluster' : f.actual_cluster_id}`
        + (f.picked_supercluster_id ? '\nvia supercluster' : ''),
    })),
  ];

  const thr = result.params?.threshold;

  return (
    <ScatterPlot2D
      className="ml-4"
      points={points}
      showDiagonal
      verticalLines={Number.isFinite(thr) ? [{ value: thr }] : []}
      xLabel="best similarity to candidate cluster [0,1]"
      yLabel="median similarity to candidate cluster"
      caption={(
        <div className="small text-muted">
          decisions vs best candidate cluster: best vs median similarity
          {' '}({hits.length} hits, {fails.length} failures)
        </div>
      )}
      legend={(
        <div className="small text-muted d-flex flex-wrap" style={{ maxWidth: 420, gap: '10px' }}>
          <span style={{ color: HIT_COLORS.joined_existing }}>● Add Ok</span>
          <span style={{ color: HIT_COLORS.created_new }}>● New </span>
          <span style={{ color: HIT_COLORS.new_excused }}>● New excused (linked cluster) </span>
          <span style={{ color: FAIL_COLORS.wrong_cluster }}>● Wrong </span>
          <span style={{ color: FAIL_COLORS.false_match }}>● False match (was New) </span>
          <span style={{ color: FAIL_COLORS.missed_match }}>● Missed (said New) </span>
          <span style={{ color: SUPERCLUSTER_COLOR }}>◯ via supercluster</span> 
          <span><span style={{ color: '#f97316' }}>┆</span> threshold </span>
          <span><span style={{ color: '#94a3b8' }}>⟍</span> median = best</span>
        </div>
      )}
    />
  );
}

function SuperclusterBadge({ superclusterId }) {
  if (!superclusterId) return null;
  return (
    <span
      className="badge badge-dark ml-1"
      title={'Pick came through a SUPERCLUSTER (linked clusters = same physical template): '
        + 'the best-matching image may belong to a linked cluster of another model. '
        + `Supercluster ${superclusterId}`}
    >
      VIA supercluster
    </span>
  );
}

function ClusteringFailureBadge({ failType }) {
  return (
    <span className={`badge badge-dark mr-2`} style={{ backgroundColor: FAIL_COLORS[failType] }} title={FAIL_TYPE_HELP[failType]}>
      {failType}
    </span>
  );
}

function DecisionBadge({ decision, imageCount, clusterId }) {
  if (decision === 'new') {
    return <span className="badge badge-primary">new</span>;
  } else {
    return <>
      <BadgeColorHash id={clusterId} />
      {imageCount > 0 ? ` (${imageCount} imgs)` : ''}
    </>;
  }
}

function ClusteringFailureRow({ failure: f, setup = '' }) {
  const { page } = useContext(LegoAdminPageContext);

  const openSimilarImagesModal = () => {
    page.openModal(
      <ImageTopSimilarityFinder imgId={String(f.image_id)} setup={setup} showControls={true} />,
      { title: <span>Images similar to test image <BadgeId id={f.image_id} /></span>, fullScreen: true },
    );
  };

  return (
    <div className="d-flex align-items-center border-bottom py-1 mb-2" style={{ background: FAIL_COLORS[f.fail_type] + '22' }}>
      <ClusteringFailureBadge failType={f.fail_type} />

      <div className="text-center mx-2">
        <ImagePreview url={toOriginalImageUrl(f.image_url)} imageId={f.image_id} img200 contain zoom title={f.image_url} />
        <div className="small">
          <BadgeId id={f.image_id} />
          <IconButton level="violeta" icon={'image_search'} className={'zoom-90'}
            title={'Find images globally most similar to this test image'}
            onClick={openSimilarImagesModal} />
        </div>
        <div className='small'>
          <span className='text-muted align-middle mr-1'>{f.image_date}</span> <TrimSpec className='align-middle' spec="modelId" value={f.model_id} />
        </div>
      </div>

      <div className="mx-2 small" style={{ minWidth: 280 }}>
        <div className={'rounded p-1 border border-light-secondary bg-light'}>
          Expected: <DecisionBadge decision={f.actual} imageCount={f.actual_cluster_pre_images} clusterId={f.actual_cluster_id} />
          <br />
          {f.actual_cluster_best_similarity != null ? (
            <span className="text-muted">
              Best: <SimilarityBadge value={f.actual_cluster_best_similarity} title="best similarity [0,1] to any pre-cutoff image" />
            </span>
          ) : null}
          {f.actual_cluster_median_similarity != null ? (
            <span className="text-muted">
              {' '}Median: <SimilarityBadge value={f.actual_cluster_median_similarity} title="median similarity [0,1] to the actual cluster's pre-cutoff images" />
            </span>
          ) : null}
        </div>

        <div className="mt-2 rounded p-1 border border-light-secondary bg-light">
          Predicted: <DecisionBadge decision={f.predicted} imageCount={f.predicted_cluster_pre_images?.length} clusterId={f.predicted_cluster_id} />
          <SuperclusterBadge superclusterId={f.picked_supercluster_id} />

          <br />
          <span className={"text-muted" + (f.predicted === 'existing' ? '' : ' px-1 d-inline-block rounded bg-light-secondary translucent')}>
            Best: <SimilarityBadge value={f.best_similarity} title="best similarity [0,1] to any pre-cutoff image" />
            {(f.best_cluster_median_similarity ?? f.predicted_cluster_median_similarity) != null ? (
              <>
                {' '}Median: <SimilarityBadge
                  value={f.best_cluster_median_similarity ?? f.predicted_cluster_median_similarity}
                  title={f.predicted === 'existing'
                    ? "median similarity [0,1] to the predicted cluster's pre-cutoff images"
                    : "median similarity [0,1] to the best candidate cluster's pre-cutoff images (declined as new)"}
                />
              </>
            ) : null}
          </span>
        </div>

        <div>
          {f.second_similarity != null ? (
            <div className="text-muted ml-0 mt-4">
              2nd Best: <SimilarityBadge value={f.second_similarity} title="best similarity [0,1] to the runner-up cluster" />
            </div>
          ) : null}
        </div>

      </div>

      {f.best_match_image_url ? (
        <div className={`text-center mx-2 rounded`} style={{ borderColor: FAIL_COLORS[f.fail_type], borderWidth: 1, borderStyle: 'solid' }}>
          <CompareImagePreview left compareUrl={toOriginalImageUrl(f.image_url)} imageId={f.best_match_image_id} url={toOriginalImageUrl(f.best_match_image_url)} img200 contain zoom title="best matching pre-cutoff image" />
          <div className="small text-muted">
            Best match{f.picked_supercluster_id ? <span className="badge badge-dark ml-1">supercluster</span> : ''}
          </div>
        </div>
      ) : null}

      <div className="mx-2">
        <div className="small text-muted">
          Predicted {f.predicted_cluster_id ? <BadgeColorHash id={f.predicted_cluster_id} /> : null}
        </div>
        {f.predicted_cluster_id ? (
          <ImageClusterPreview clusterId={f.predicted_cluster_id} size="50" maxImages={5} highlightImageIds={f.predicted_cluster_pre_image_ids} />
        ) : (
          <span
            className="border bg-light rounded p-1 d-inline-block small text-muted"
            style={{ minHeight: 72 }}
          >
            new cluster
          </span>
        )}
      </div>

      <div className="mx-2">
        <div className="small text-muted">
          Expected: <BadgeColorHash id={f.actual_cluster_id} />
        </div>
        <ImageClusterPreview clusterId={f.actual_cluster_id} size="50" maxImages={5} highlightImageIds={f.actual_cluster_pre_image_ids.concat(['NOTGONNAMATCH'])} />
      </div>

      <a
        className="btn btn-sm btn-outline-secondary ml-2 py-0"
        target="_blank"
        rel="noreferrer"
        href={clusterPairAdminUrl(f.actual_cluster_id, f.predicted_cluster_id)}
      >
        ⧉ clusters
      </a>
    </div>
  );
}

export default function FuseboxClusteringBenchmarkResult({
  result,
  saveLabel = '',
  onSaveLabelChange,
  onSave,
}) {
  if (!result) return null;
  const s = result.summary;

  return (
    <div className="border rounded p-3 mb-4">
      <h5>
        Clustering simulation — decision accuracy {(s.decision_accuracy * 100).toFixed(1)}%,
        full {(s.full_accuracy * 100).toFixed(1)}%
      </h5>
      <RunHeader result={result} />
      <div className="d-flex flex-wrap">
        <SummaryTable
          summary={s}
          fields={[
            ['cutoff_date', 'cutoff date'],
            ['sampling_order', 'image sampling'],
            ['images_tested', 'images tested'],
            ['actual_new', 'actual: founded new cluster'],
            ['actual_existing', 'actual: joined existing'],
            ['decision_accuracy', 'decision accuracy (new vs existing)', true],
            ['full_accuracy', 'full accuracy (decision + right cluster)', true],
            ['cluster_pick_strict', 'cluster pick — exact', true],
            ['cluster_pick_group', 'cluster pick — same template group', true],
            ['new_excused_same_group', '"new" excused — matched a linked cluster'],
            ['supercluster_picks', 'picks via supercluster (linked clusters)'],
            ['best_threshold', 'best threshold (decision)'],
            ['best_threshold_accuracy', 'decision accuracy @ best', true],
            ['cluster_type', 'cluster type'],
            ['clusters_total', 'clusters in db'],
            ['clusters_existing_at_cutoff', 'clusters existing at cutoff'],
            ['evaluable_pool', 'evaluable images after cutoff'],
            ['skipped_unreproducible', 'skipped (cluster founded after cutoff)'],
          ]}
        />
        <div className="ml-4">
          <ThresholdSweep sweep={result.sweep} bestThreshold={s.best_threshold} />
          <table className="table table-sm w-auto small">
            <thead>
              <tr>
                <th />
                <th>pred. new</th>
                <th>pred. existing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th className="text-muted">actual new</th>
                <td>{s.confusion.actual_new.predicted_new}</td>
                <td>{s.confusion.actual_new.predicted_existing}</td>
              </tr>
              <tr>
                <th className="text-muted">actual existing</th>
                <td>{s.confusion.actual_existing.predicted_new}</td>
                <td>{s.confusion.actual_existing.predicted_existing}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ClusteringHitsScatter result={result} />
      </div>
      {onSave ? (
        <div className="d-flex align-items-center mb-3">
          <input
            type="text"
            placeholder="run label, e.g. head_v2"
            className="form-control form-control-sm mr-2"
            style={{ width: 220 }}
            value={saveLabel}
            onChange={(e) => onSaveLabelChange?.(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={!saveLabel.trim()}
            onClick={() => onSave(result)}
          >
            Save run for comparison
          </button>
        </div>
      ) : null}
      <h6>Failures ({result.failures.length} of {s.failures_total})</h6>
      {result.failures.map((f, i) => (
        <ClusteringFailureRow key={i} failure={f}
          setup={result.embedding?.setupName || result.params?.setup || ''} />
      ))}
    </div>
  );
}
